// shabba-dashboard/src/components/analytics/VWAPMicropriceAnalyzer.tsx
// Comparative analysis of VWAP vs Microprice for advanced market microstructure insights

import React, { useEffect, useMemo, useRef } from 'react';
import { TrendingUp, TrendingDown, Scale, Activity, Target, BarChart3 } from 'lucide-react';
import { useMarket } from '../../context/MarketContext';

interface VWAPData {
  value: number;
  volume: number;
  trades: number;
  trend: 'bullish' | 'bearish' | 'neutral';
  distance: number;
  distancePercent: number;
}

interface MicropriceData {
  value: number;
  bidWeight: number;
  askWeight: number;
  imbalance: number;
  confidence: number;        // 0..1
  predictiveEdge: number;    // bps
  midPrice: number;          // expose for display
}

interface ComparisonMetrics {
  vwapMicroDiff: number;
  vwapMicroDiffPercent: number;
  consensusSignal: 'aligned' | 'divergent' | 'conflicted';
  tradingSignal: 'buy' | 'sell' | 'neutral';
  signalStrength: 'strong' | 'moderate' | 'weak';
  expectedDirection: 'up' | 'down' | 'sideways';
}

interface VWAPMicropriceAnalyzerProps {
  timeWindow?: number;              // minutes
  showDetailedBreakdown?: boolean;
}

export default function VWAPMicropriceAnalyzer({
  timeWindow = 60,
  showDetailedBreakdown = true
}: VWAPMicropriceAnalyzerProps) {
  const {
    selectedSymbol,
    currentPrice,
    recentTrades,
    orderBook,
    labelForSecId,
    vwapDataBySymbol,
    micropriceDataBySymbol,
    dataVersions,
    stats
  } = useMarket();

  // Track latest processed versions
  const lastProcessedVwapVersionRef = useRef<number>(0);
  const lastProcessedMicropriceVersionRef = useRef<number>(0);
  
  // Get latest VWAP and microprice from WebSocket data
  const symbolKey = selectedSymbol.toUpperCase();
  const wsVwapData = vwapDataBySymbol.get(symbolKey) || [];
  const wsMicropriceData = micropriceDataBySymbol.get(symbolKey) || [];
  const symbolVersions = dataVersions[symbolKey];
  
  // Update when versions change (reactive to WebSocket updates)
  useEffect(() => {
    if (!symbolVersions) return;
    
    const vwapVersion = symbolVersions.vwap || 0;
    const micropriceVersion = symbolVersions.microprice || 0;
    
    // Track processed versions
    if (vwapVersion > lastProcessedVwapVersionRef.current) {
      lastProcessedVwapVersionRef.current = vwapVersion;
    }
    if (micropriceVersion > lastProcessedMicropriceVersionRef.current) {
      lastProcessedMicropriceVersionRef.current = micropriceVersion;
    }
  }, [symbolVersions?.vwap, symbolVersions?.microprice, selectedSymbol]);
  
  // Reset version refs when symbol changes
  useEffect(() => {
    lastProcessedVwapVersionRef.current = 0;
    lastProcessedMicropriceVersionRef.current = 0;
  }, [selectedSymbol]);
  
  const loading = false; // No loading state needed for WebSocket data

  // Use VPS-provided VWAP and Microprice data (from WebSocket)
  // No client-side calculations - data should come from VPS
  const analytics = useMemo(() => {
    if (!currentPrice) return null;
    
    // Get latest VWAP and microprice from WebSocket data (VPS-provided)
    const latestVwap = wsVwapData.length > 0 ? wsVwapData[wsVwapData.length - 1] : null;
    const latestMicroprice = wsMicropriceData.length > 0 ? wsMicropriceData[wsMicropriceData.length - 1] : null;
    
    // Require VPS data - no fallback calculations
    const vwapValue = latestVwap?.vwap ?? stats?.vwap;
    const micropriceValue = latestMicroprice?.microprice;
    
    if (!vwapValue || !micropriceValue) return null;

    // Use VPS-provided imbalance and spread
    const imbalance = latestMicroprice?.imbalance ?? 0;
    const spread = latestMicroprice?.spread ?? (orderBook.bids.length > 0 && orderBook.asks.length > 0 
      ? orderBook.asks[0].price - orderBook.bids[0].price 
      : 0);

    // VWAP analysis using VPS data
    const vwapAnalysis: VWAPData = {
      value: vwapValue,
      volume: stats?.volume ?? 0,
      trades: recentTrades.length, // Use recent trades count as proxy
      trend: vwapValue > currentPrice ? 'bearish' : 'bullish',
      distance: vwapValue - currentPrice,
      distancePercent: ((vwapValue - currentPrice) / currentPrice) * 100
    };

    // Microprice analysis using VPS data
    // Calculate bid/ask weights from order book for display purposes only
    const bidSize = orderBook.bids.reduce((sum, b) => sum + b.size, 0);
    const askSize = orderBook.asks.reduce((sum, a) => sum + a.size, 0);
    const totalSize = bidSize + askSize;
    const bidWeight = totalSize > 0 ? bidSize / totalSize : (imbalance < 0 ? 0.3 : 0.7);
    const askWeight = totalSize > 0 ? askSize / totalSize : (imbalance > 0 ? 0.3 : 0.7);
    
    const micropriceAnalysis: MicropriceData = {
      value: micropriceValue, // From VPS
      bidWeight: bidWeight,
      askWeight: askWeight,
      imbalance: imbalance, // From VPS
      confidence: Math.min(0.9, Math.abs(imbalance) * 2 + 0.1), // Confidence based on imbalance strength
      predictiveEdge: Math.abs(spread) * 10000, // Convert to bps
      midPrice: latestMicroprice?.bidPrice && latestMicroprice?.askPrice
        ? (latestMicroprice.bidPrice + latestMicroprice.askPrice) / 2
        : currentPrice
    };

    // Comparison Analysis
    const vwapMicroDiff = vwapValue - micropriceValue;
    const vwapMicroDiffPercent = (vwapMicroDiff / micropriceValue) * 100;

    // Determine consensus signal
    let consensusSignal: ComparisonMetrics['consensusSignal'] = 'aligned';
    if (Math.abs(vwapMicroDiffPercent) > 0.1) {
      consensusSignal = Math.abs(vwapMicroDiffPercent) > 0.5 ? 'conflicted' : 'divergent';
    }

    // Generate trading signal based on both indicators
    let tradingSignal: ComparisonMetrics['tradingSignal'] = 'neutral';
    let signalStrength: ComparisonMetrics['signalStrength'] = 'weak';
    let expectedDirection: ComparisonMetrics['expectedDirection'] = 'sideways';

    const vwapSignal = currentPrice < vwapValue ? 'buy' : currentPrice > vwapValue ? 'sell' : 'neutral';
    const microSignal = currentPrice < micropriceValue ? 'buy' : currentPrice > micropriceValue ? 'sell' : 'neutral';

    if (vwapSignal === microSignal && vwapSignal !== 'neutral') {
      tradingSignal = vwapSignal;
      signalStrength = Math.abs(vwapAnalysis.distancePercent) > 0.5 && micropriceAnalysis.predictiveEdge > 2 ? 'strong' : 'moderate';
      expectedDirection = vwapSignal === 'buy' ? 'up' : 'down';
    } else if (vwapSignal !== 'neutral' && microSignal !== 'neutral') {
      const vwapConfidence = Math.min(0.8, Math.abs(vwapAnalysis.distancePercent) / 2);
      if (micropriceAnalysis.confidence > vwapConfidence) {
        tradingSignal = microSignal;
        signalStrength = 'weak';
        expectedDirection = microSignal === 'buy' ? 'up' : 'down';
      } else {
        tradingSignal = vwapSignal;
        signalStrength = 'weak';
        expectedDirection = vwapSignal === 'buy' ? 'up' : 'down';
      }
    }

    const comparisonMetrics: ComparisonMetrics = {
      vwapMicroDiff,
      vwapMicroDiffPercent,
      consensusSignal,
      tradingSignal,
      signalStrength,
      expectedDirection
    };

    return {
      vwapData: vwapAnalysis,
      micropriceData: micropriceAnalysis,
      comparisonMetrics,
      currentPrice,
      midPrice: currentPrice
    };
  }, [wsVwapData, wsMicropriceData, currentPrice, stats, orderBook]);

  if (loading) {
    return (
      <div style={{ backgroundColor: '#1e293b', borderRadius: 8, padding: 16, border: '1px solid #334155' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <Scale size={16} color="#f59e0b" />
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#f1f5f9' }}>
            VWAP vs Microprice Analysis
          </h3>
        </div>
        <div style={{ color: '#94a3b8', fontSize: 12, textAlign: 'center', padding: '20px 0' }}>
          Loading VWAP and microprice data...
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div style={{ backgroundColor: '#1e293b', borderRadius: 8, padding: 16, border: '1px solid #334155' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <Scale size={16} color="#f59e0b" />
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#f1f5f9' }}>
            VWAP vs Microprice Analysis
          </h3>
        </div>
        <div style={{ color: '#94a3b8', fontSize: 12, textAlign: 'center', padding: '20px 0' }}>
          Insufficient data for comparison analysis
          <br />
          <span style={{ fontSize: 11, color: '#64748b' }}>
            Need VWAP and microprice data from database
          </span>
        </div>
      </div>
    );
  }

  const { vwapData, micropriceData, comparisonMetrics } = analytics;

  if (!comparisonMetrics) {
    return (
      <div style={{ backgroundColor: '#1e293b', borderRadius: 8, padding: 16, border: '1px solid #334155' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <Scale size={16} color="#f59e0b" />
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#f1f5f9' }}>
            VWAP vs Microprice Analysis
          </h3>
        </div>
        <div style={{ color: '#94a3b8', fontSize: 12, textAlign: 'center', padding: '20px 0' }}>
          Insufficient data for comparison analysis
          <br />
          <span style={{ fontSize: 11, color: '#64748b' }}>
            Need trade series (DB) and order book data
          </span>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount: number) => `${amount.toFixed(2)} TZS`;
  const formatPercent = (percent: number) => `${percent >= 0 ? '+' : ''}${percent.toFixed(3)}%`;
  const formatBps = (bps: number) => `${bps.toFixed(1)} bps`;

  const getSignalColor = (signal: string) => {
    switch (signal) {
      case 'buy': case 'up': return '#10b981';
      case 'sell': case 'down': return '#ef4444';
      case 'strong': return '#3b82f6';
      case 'moderate': return '#f59e0b';
      default: return '#94a3b8';
    }
  };

  const getSignalIcon = (signal: string) => {
    switch (signal) {
      case 'buy': case 'up': return <TrendingUp size={12} />;
      case 'sell': case 'down': return <TrendingDown size={12} />;
      default: return <Activity size={12} />;
    }
  };

  const getConsensusColor = (consensus: ComparisonMetrics['consensusSignal']) => {
    switch (consensus) {
      case 'aligned': return '#10b981';
      case 'divergent': return '#f59e0b';
      case 'conflicted': return '#ef4444';
      default: return '#94a3b8';
    }
  };

  return (
    <div style={{ backgroundColor: '#1e293b', borderRadius: 8, padding: 16, border: '1px solid #334155' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Scale size={16} color="#3b82f6" />
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#f1f5f9' }}>
            VWAP vs Microprice Analysis
          </h3>
        </div>
        <div style={{
          backgroundColor: `${getConsensusColor(comparisonMetrics.consensusSignal)}15`,
          border: `1px solid ${getConsensusColor(comparisonMetrics.consensusSignal)}30`,
          borderRadius: 4,
          padding: '4px 8px',
          fontSize: 11,
          fontWeight: 600,
          color: getConsensusColor(comparisonMetrics.consensusSignal),
          textTransform: 'capitalize'
        }}>
          {comparisonMetrics.consensusSignal}
        </div>
      </div>

      {/* Main Comparison */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
        {/* VWAP */}
        <div style={{ backgroundColor: '#0f172a', borderRadius: 6, padding: 12, border: '1px solid #334155', textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginBottom: 8 }}>
            <BarChart3 size={12} color="#3b82f6" />
            <span style={{ fontSize: 11, color: '#3b82f6', fontWeight: 600 }}>VWAP</span>
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#f1f5f9', marginBottom: 4 }}>
            {formatCurrency(vwapData.value)}
          </div>
          <div style={{ fontSize: 10, color: getSignalColor(vwapData.trend), marginBottom: 2 }}>
            {formatPercent(vwapData.distancePercent)} from current
          </div>
          <div style={{ fontSize: 9, color: '#64748b' }}>
            {vwapData.trades} trades • {(vwapData.volume / 1000).toFixed(1)}K vol
          </div>
        </div>

        {/* Current Price */}
        <div style={{ backgroundColor: '#0f172a', borderRadius: 6, padding: 12, border: '1px solid #f59e0b30', textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginBottom: 8 }}>
            <Target size={12} color="#f59e0b" />
            <span style={{ fontSize: 11, color: '#f59e0b', fontWeight: 600 }}>CURRENT</span>
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#f59e0b', marginBottom: 4 }}>
            {formatCurrency(currentPrice)}
          </div>
          <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 2 }}>
            Market Price
          </div>
          <div style={{ fontSize: 9, color: '#64748b' }}>
            {labelForSecId(selectedSymbol)}
          </div>
        </div>

        {/* Microprice */}
        <div style={{ backgroundColor: '#0f172a', borderRadius: 6, padding: 12, border: '1px solid #334155', textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginBottom: 8 }}>
            <Activity size={12} color="#10b981" />
            <span style={{ fontSize: 11, color: '#10b981', fontWeight: 600 }}>MICROPRICE</span>
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#f1f5f9', marginBottom: 4 }}>
            {formatCurrency(micropriceData.value)}
          </div>
          <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 2 }}>
            {formatBps(micropriceData.predictiveEdge)} edge
          </div>
          <div style={{ fontSize: 9, color: '#64748b' }}>
            {(micropriceData.confidence * 100).toFixed(0)}% confidence
          </div>
        </div>
      </div>

      {/* Trading Signal */}
      <div style={{
        backgroundColor: '#0f172a',
        borderRadius: 6,
        padding: 16,
        marginBottom: showDetailedBreakdown ? 16 : 0,
        border: `1px solid ${getSignalColor(comparisonMetrics.tradingSignal)}30`,
        textAlign: 'center'
      }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          backgroundColor: `${getSignalColor(comparisonMetrics.tradingSignal)}15`,
          border: `1px solid ${getSignalColor(comparisonMetrics.tradingSignal)}30`,
          borderRadius: 6,
          padding: '8px 16px',
          marginBottom: 8
        }}>
          {getSignalIcon(comparisonMetrics.tradingSignal)}
          <span style={{ fontSize: 14, fontWeight: 700, color: getSignalColor(comparisonMetrics.tradingSignal), textTransform: 'uppercase' }}>
            {comparisonMetrics.tradingSignal}
          </span>
          <span style={{ fontSize: 12, color: getSignalColor(comparisonMetrics.signalStrength), textTransform: 'capitalize' }}>
            ({comparisonMetrics.signalStrength})
          </span>
        </div>

        <div style={{ fontSize: 12, color: '#94a3b8' }}>
          Expected Direction: <span style={{ color: getSignalColor(comparisonMetrics.expectedDirection), fontWeight: 600, textTransform: 'capitalize' }}>
            {comparisonMetrics.expectedDirection}
          </span>
        </div>

        <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>
          VWAP-Microprice Diff: {formatCurrency(Math.abs(comparisonMetrics.vwapMicroDiff))} ({formatPercent(comparisonMetrics.vwapMicroDiffPercent)})
        </div>
      </div>

      {/* Detailed Breakdown */}
      {showDetailedBreakdown && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {/* VWAP Details */}
          <div style={{ backgroundColor: '#0f172a', borderRadius: 6, padding: 12 }}>
            <div style={{ fontSize: 12, color: '#3b82f6', fontWeight: 600, marginBottom: 8 }}>VWAP Analysis</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748b' }}>Value:</span>
                <span style={{ color: '#f1f5f9', fontWeight: 600 }}>{formatCurrency(vwapData.value)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748b' }}>Distance:</span>
                <span style={{ color: vwapData.distance > 0 ? '#10b981' : '#ef4444', fontWeight: 600 }}>
                  {formatPercent(vwapData.distancePercent)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748b' }}>Trend:</span>
                <span style={{ color: vwapData.trend === 'bullish' ? '#10b981' : vwapData.trend === 'bearish' ? '#ef4444' : '#94a3b8',
                               fontWeight: 600, textTransform: 'capitalize' }}>
                  {vwapData.trend}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748b' }}>Volume:</span>
                <span style={{ color: '#f1f5f9', fontWeight: 600 }}>
                  {(vwapData.volume / 1000).toFixed(1)}K
                </span>
              </div>
            </div>
          </div>

          {/* Microprice Details */}
          <div style={{ backgroundColor: '#0f172a', borderRadius: 6, padding: 12 }}>
            <div style={{ fontSize: 12, color: '#10b981', fontWeight: 600, marginBottom: 8 }}>Microprice Analysis</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748b' }}>Value:</span>
                <span style={{ color: '#f1f5f9', fontWeight: 600 }}>{formatCurrency(micropriceData.value)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748b' }}>Imbalance:</span>
                <span style={{ color: micropriceData.imbalance > 0 ? '#10b981' : '#ef4444', fontWeight: 600 }}>
                  {(micropriceData.imbalance * 100).toFixed(1)}%
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748b' }}>Confidence:</span>
                <span style={{ color: micropriceData.confidence > 0.7 ? '#10b981' : '#f59e0b', fontWeight: 600 }}>
                  {(micropriceData.confidence * 100).toFixed(0)}%
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748b' }}>Pred. Edge:</span>
                <span style={{ color: '#f1f5f9', fontWeight: 600 }}>
                  {formatBps(micropriceData.predictiveEdge)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Methodology Note */}
      <div style={{ marginTop: 12, padding: 8, backgroundColor: '#0f172a', borderRadius: 4, border: '1px solid #334155' }}>
        <div style={{ fontSize: 10, color: '#94a3b8', lineHeight: 1.4 }}>
          <strong style={{ color: '#3b82f6' }}>Methodology:</strong> VWAP = volume-weighted execution benchmark over {timeWindow}min window.
          Microprice = order book imbalance-adjusted fair price. Signals combine both with confidence weighting.
        </div>
      </div>
    </div>
  );
}
